// Assisted social sharing. UGAIN never posts on an ambassador's behalf and
// never claims a post succeeded. Order of preference:
//   1. Web Share API (with the image file when the browser supports it)
//   2. Platform web intent URL (X, WhatsApp, Facebook, LinkedIn)
//   3. Copy caption + link to clipboard (Instagram, TikTok and fallback)
// Native packaging (Capacitor) can swap (1) for @capacitor/share.
import type { ShareKit } from '../domain/types';

export const PLATFORMS = ['Instagram', 'TikTok', 'LinkedIn', 'X', 'WhatsApp', 'Facebook'] as const;
export type Platform = (typeof PLATFORMS)[number];

export function campaignUrl(kit: ShareKit): string {
  const base = import.meta.env.VITE_SHARE_BASE_URL || 'https://share.ugain.example/';
  return `${base.replace(/\/?$/, '/')}${kit.path}`;
}

export const captionWithTags = (kit: ShareKit, variant: 'short' | 'story'): string => `${kit.captions[variant]} ${kit.hashtags.join(' ')}`;

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export type ShareOutcome = 'shared' | 'cancelled' | 'unsupported' | 'failed';

export async function nativeShare(opts: { title: string; text: string; url: string; imageUrl?: string }): Promise<ShareOutcome> {
  if (typeof navigator === 'undefined' || !navigator.share) return 'unsupported';
  let files: File[] | undefined;
  if (opts.imageUrl && navigator.canShare) {
    try {
      const blob = await (await fetch(opts.imageUrl)).blob();
      const file = new File([blob], opts.imageUrl.split('/').pop() || 'ugain.webp', { type: blob.type || 'image/webp' });
      if (navigator.canShare({ files: [file] })) files = [file];
    } catch {
      files = undefined;
    }
  }
  try {
    await navigator.share({ title: opts.title, text: opts.text, url: opts.url, ...(files ? { files } : {}) });
    return 'shared';
  } catch (e) {
    return e instanceof DOMException && e.name === 'AbortError' ? 'cancelled' : 'failed';
  }
}

/** Web intent for platforms that publish one; null means copy-and-open-app. */
export function intentUrl(p: Platform, text: string, url: string): string | null {
  const t = encodeURIComponent(text);
  const u = encodeURIComponent(url);
  switch (p) {
    case 'X':
      return `https://x.com/intent/post?text=${t}&url=${u}`;
    case 'WhatsApp':
      return `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    case 'Facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'LinkedIn':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    default:
      return null;
  }
}

export const isHttpUrl = (s: string): boolean => {
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};
